'use strict';

// Sprint 19a: status order swapped — prepayment unlocks "in_progress",
// final payment can come later (sometimes after the tour started).
const ESTIMATE_STATUSES = [
    'draft', 'sent', 'confirmed', 'prepaid',
    'in_progress', 'paid', 'completed', 'closed'
];

// Sprint 19a: 3-phase grouping for compact rendering on the calculator
// (variant "b" — compact line with groups instead of 8-step strip).
const PHASE_GROUPS = [
    { id: 'sales',    label: 'Продажа',    statuses: ['draft', 'sent', 'confirmed'] },
    { id: 'finance',  label: 'Оплаты',     statuses: ['prepaid', 'paid'] },
    { id: 'ops',      label: 'Операции',   statuses: ['in_progress', 'completed', 'closed'] }
];

const STATUS_CONFIG = {
    draft:       { label: 'Черновик',     bg: '#f1f5f9', text: '#475569', icon: 'file-edit',     phase: 'sales' },
    sent:        { label: 'Отправлена',   bg: '#dbeafe', text: '#1d4ed8', icon: 'send',          phase: 'sales' },
    confirmed:   { label: 'Подтверждена', bg: '#fef3c7', text: '#92400e', icon: 'check-circle',  phase: 'sales' },
    prepaid:     { label: 'Предоплата',   bg: '#fce7f3', text: '#9d174d', icon: 'credit-card',   phase: 'finance' },
    in_progress: { label: 'В работе',     bg: '#e0e7ff', text: '#3730a3', icon: 'plane',         phase: 'ops' },
    paid:        { label: 'Оплачена',     bg: '#d1fae5', text: '#065f46', icon: 'banknote',      phase: 'finance' },
    completed:   { label: 'Завершена',    bg: '#dcfce7', text: '#166534', icon: 'check-circle-2', phase: 'ops' },
    closed:      { label: 'Закрыта',      bg: '#f3f4f6', text: '#374151', icon: 'archive',       phase: 'ops' },
    cancelled:   { label: 'Отменена',     bg: '#fee2e2', text: '#991b1b', icon: 'x-circle',      phase: null }
};

const REVERSE_TRANSITIONS = {
    sent:        'draft',
    confirmed:   'sent',
    prepaid:     'confirmed',
    in_progress: 'prepaid',
    paid:        'in_progress',
    completed:   'paid',
    closed:      'completed'
};

function getNextStatus(current) {
    const idx = ESTIMATE_STATUSES.indexOf(current);
    if (idx === -1 || idx >= ESTIMATE_STATUSES.length - 1) return null;
    return ESTIMATE_STATUSES[idx + 1];
}

function getReverseStatus(current) {
    return REVERSE_TRANSITIONS[current] || null;
}

function renderPhaseGroups(currentStatus) {
    // Where is the current status in the linear order?
    const currentIdx = ESTIMATE_STATUSES.indexOf(currentStatus);
    if (currentIdx === -1) return '';

    const currentPhase = STATUS_CONFIG[currentStatus]?.phase;

    return PHASE_GROUPS.map(group => {
        // Phase state: done / current / pending
        const phaseStartIdx = ESTIMATE_STATUSES.indexOf(group.statuses[0]);
        const phaseEndIdx = ESTIMATE_STATUSES.indexOf(group.statuses[group.statuses.length - 1]);
        const isDone = currentIdx > phaseEndIdx && currentStatus !== 'cancelled';
        const isCurrent = currentPhase === group.id;
        const cls = isDone ? 'phase-done' : isCurrent ? 'phase-current' : 'phase-pending';

        // Sub-steps within phase
        const dots = group.statuses.map(s => {
            const sIdx = ESTIMATE_STATUSES.indexOf(s);
            const dotCls = sIdx < currentIdx ? 'dot-done'
                : sIdx === currentIdx ? 'dot-current'
                : 'dot-pending';
            return `<span class="phase-dot ${dotCls}" title="${STATUS_CONFIG[s].label}"></span>`;
        }).join('');

        return `
            <div class="phase-group ${cls}">
                <div class="phase-label">${group.label}</div>
                <div class="phase-dots">${dots}</div>
            </div>
        `;
    }).join('<div class="phase-separator"></div>');
}

function renderEstimateStatusBar(estimateData) {
    const status = estimateData?.status || 'draft';
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const nextStatus = estimateData?.nextStatus || getNextStatus(status);
    const nextConfig = nextStatus ? STATUS_CONFIG[nextStatus] : null;
    const reverseStatus = estimateData?.reverseStatus || getReverseStatus(status);
    const reverseConfig = reverseStatus ? STATUS_CONFIG[reverseStatus] : null;
    const allowed = estimateData?.allowedTransitions || [];

    // Sprint 19a: блокируем переход на in_progress если нет approval предоплаты
    const blockNextDueToPrepayment = nextStatus === 'in_progress'
        && !estimateData?.prepaymentApprovedAt;
    const blockTooltip = blockNextDueToPrepayment
        ? 'Нельзя: нет апрува "Предоплата получена" (поставьте галку в шапке сметы — для бесплатной предоплаты тоже)'
        : null;

    const bar = document.createElement('div');
    bar.className = 'estimate-status-bar';
    bar.id = 'estimate-status-bar';

    const phasesHtml = status === 'cancelled'
        ? `<span class="status-cancelled-label" style="background:${STATUS_CONFIG.cancelled.bg};color:${STATUS_CONFIG.cancelled.text};">
              <i data-lucide="x-circle" style="width:13px;height:13px;vertical-align:-2px"></i> Отменена
           </span>`
        : renderPhaseGroups(status);

    // Sprint 19b: track flags row — bookings/tour/suppliers/reconciliation
    // Показываем только если статус достиг in_progress и/или хотя бы 1 флаг есть.
    const isOpsPhase = ['in_progress', 'paid', 'completed', 'closed'].includes(status);
    const trackFlags = [];
    if (isOpsPhase || estimateData?.bookingsCompleteAt) {
        trackFlags.push({
            id: 'bookings',
            label: 'Бронирования',
            icon: 'calendar-check',
            done: !!estimateData?.bookingsCompleteAt,
            ts: estimateData?.bookingsCompleteAt
        });
    }
    if (isOpsPhase || estimateData?.tourStartedAt || estimateData?.tourEndedAt) {
        trackFlags.push({
            id: 'tour-start',
            label: 'Тур начался',
            icon: 'play',
            done: !!estimateData?.tourStartedAt,
            ts: estimateData?.tourStartedAt
        });
        trackFlags.push({
            id: 'tour-end',
            label: 'Тур закончился',
            icon: 'flag',
            done: !!estimateData?.tourEndedAt,
            ts: estimateData?.tourEndedAt
        });
    }
    if (isOpsPhase || estimateData?.suppliersPaidAt) {
        trackFlags.push({
            id: 'suppliers-paid',
            label: 'Поставщики оплачены',
            icon: 'banknote',
            done: !!estimateData?.suppliersPaidAt,
            ts: estimateData?.suppliersPaidAt
        });
    }

    const tracksHtml = trackFlags.length === 0 ? '' : `
        <div class="status-bar-tracks">
            ${trackFlags.map(t => {
                const tsLabel = t.ts ? new Date(t.ts * 1000).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }) : '';
                return `<span class="track-flag ${t.done ? 'track-done' : 'track-pending'}" title="${t.label}${tsLabel ? ' · ' + tsLabel : ''}">
                    <i data-lucide="${t.done ? 'check-circle-2' : t.icon}" style="width:11px;height:11px;vertical-align:-2px"></i>
                    <span class="track-label">${t.label}</span>
                    ${tsLabel ? `<span class="track-ts">${tsLabel}</span>` : ''}
                </span>`;
            }).join('')}
        </div>
    `;

    bar.innerHTML = `
        <div class="status-bar-inner">
            <div class="status-bar-left">
                <span class="status-badge" style="background:${config.bg};color:${config.text}">
                    <i data-lucide="${config.icon}" style="width:13px;height:13px;vertical-align:-2px"></i>
                    ${config.label}
                </span>
            </div>
            <div class="status-bar-pipeline">${phasesHtml}</div>
            <div class="status-bar-right">
                ${reverseConfig && status !== 'cancelled' && status !== 'closed' ? `
                    <button class="btn-reverse-status" data-prev="${reverseStatus}" title="Откатить на: ${reverseConfig.label} (требуется комментарий)">
                        <i data-lucide="undo-2" style="width:13px;height:13px"></i>
                    </button>
                ` : ''}
                ${nextConfig && status !== 'cancelled' && status !== 'closed' ? `
                    <button class="btn-advance-status ${blockNextDueToPrepayment ? 'btn-blocked' : ''}"
                            data-next="${nextStatus}"
                            title="${blockTooltip || 'Перевести в: ' + nextConfig.label}">
                        ${nextConfig.label} <i data-lucide="arrow-right" style="width:13px;height:13px;vertical-align:-2px"></i>
                    </button>
                ` : ''}
                ${allowed.includes('cancelled') ? `
                    <button class="btn-cancel-status" title="Отменить смету">
                        <i data-lucide="x" style="width:13px;height:13px"></i>
                    </button>
                ` : ''}
                ${status === 'cancelled' ? `
                    <button class="btn-reopen-status" title="Вернуть в Черновик">
                        <i data-lucide="rotate-ccw" style="width:13px;height:13px;vertical-align:-2px"></i> Вернуть
                    </button>
                ` : ''}
            </div>
        </div>
        ${tracksHtml}
    `;

    // Sprint 22 W4 fix: lucide.createIcons с {nodes:[bar]} не всегда находит
    // свежевставленные <i data-lucide> элементы. Используем [data-lucide] selector
    // или fallback на full-document scan.
    if (typeof lucide !== 'undefined' && lucide.createIcons) {
        setTimeout(() => {
            try {
                const nodes = bar.querySelectorAll('[data-lucide]');
                if (nodes.length) lucide.createIcons({ nodes: Array.from(nodes) });
                else lucide.createIcons();
            } catch (_) {
                try { lucide.createIcons(); } catch (__) {}
            }
        }, 0);
    }

    return bar;
}

function bindStatusBarEvents(estimateId, apiClient, onTransition) {
    const bar = document.getElementById('estimate-status-bar');
    if (!bar) return;

    // Sprint 19c: защита от double-click race
    let _isTransitioning = false;
    const guarded = async (btn, fn) => {
        if (_isTransitioning) return;
        if (btn.disabled) return;
        _isTransitioning = true;
        btn.disabled = true;
        btn.style.opacity = '0.6';
        try {
            await fn();
        } finally {
            _isTransitioning = false;
            btn.disabled = false;
            btn.style.opacity = '';
        }
    };

    bar.addEventListener('click', async (e) => {
        const advanceBtn = e.target.closest('.btn-advance-status');
        const reverseBtn = e.target.closest('.btn-reverse-status');
        const cancelBtn = e.target.closest('.btn-cancel-status');
        const reopenBtn = e.target.closest('.btn-reopen-status');

        if (advanceBtn) {
            const nextStatus = advanceBtn.dataset.next;
            const nextLabel = STATUS_CONFIG[nextStatus]?.label || nextStatus;
            // Sprint 19e: optional comment при advance — для аудит-trail.
            // Hold Shift while clicking чтобы пропустить prompt (быстрый advance).
            let comment = null;
            if (!e.shiftKey) {
                comment = prompt(`Перевести в "${nextLabel}". Комментарий (необязательно, можно пропустить):`);
                if (comment === null) return; // cancel
                comment = comment.trim() || null;
            }
            await guarded(advanceBtn, async () => {
                const result = await apiClient.transitionEstimateStatus(estimateId, nextStatus, comment);
                if (result.ok) {
                    if (onTransition) onTransition(result.data);
                } else {
                    alert(result.message || 'Ошибка перехода статуса');
                }
            });
            return;
        }

        if (reverseBtn) {
            const prevStatus = reverseBtn.dataset.prev;
            const comment = prompt(`Откатить на статус "${STATUS_CONFIG[prevStatus]?.label}"? Укажите причину (обязательно):`);
            if (!comment || !comment.trim()) {
                if (comment !== null) alert('Откат требует комментария');
                return;
            }
            await guarded(reverseBtn, async () => {
                const result = await apiClient.transitionEstimateStatus(estimateId, prevStatus, comment.trim(), true);
                if (result.ok) {
                    if (onTransition) onTransition(result.data);
                } else {
                    alert(result.message || 'Ошибка отката статуса');
                }
            });
            return;
        }

        if (cancelBtn) {
            const reason = prompt('Причина отмены (необязательно):');
            if (reason === null) return;
            await guarded(cancelBtn, async () => {
                const result = await apiClient.transitionEstimateStatus(estimateId, 'cancelled', reason || null);
                if (result.ok) {
                    if (onTransition) onTransition(result.data);
                } else {
                    alert(result.message || 'Ошибка отмены');
                }
            });
            return;
        }

        if (reopenBtn) {
            await guarded(reopenBtn, async () => {
                const result = await apiClient.transitionEstimateStatus(estimateId, 'draft', 'Reopened');
                if (result.ok) {
                    if (onTransition) onTransition(result.data);
                } else {
                    alert(result.message || 'Ошибка возврата');
                }
            });
        }
    });
}

if (typeof window !== 'undefined') {
    window.EstimateStatusBar = {
        STATUS_CONFIG,
        ESTIMATE_STATUSES,
        PHASE_GROUPS,
        render: renderEstimateStatusBar,
        bind: bindStatusBarEvents
    };
}
